// Wave D — Uploads/Posts store. Backend-backed.
//
// Same pattern as Wave C: writes go to FastAPI (/api/posts/*) and on success
// we mirror the canonical record into the legacy `content_uploads` localStorage
// shape used by `useUploads()` and PostsSection.js. Reads stay sync from cache.
//
// Legacy shape preserved for backward compatibility with existing UI:
//   { id, barberId, barberName, barberUsername, barberAvatar,
//     contentType, category, description, location, date, tags,
//     beforePhotoUrl, afterPhotoUrl, videoUrl, photoUrl,
//     status: 'pending_approval' | 'approved' | 'rejected',
//     createdAt, approvedAt?, rejectedAt?, adminNotes?,
//     __backendId, __postId }   // __backendId === id when source is backend
//
// Backend → legacy mapping rules:
//   * status: pending → pending_approval; approved/rejected pass-through;
//     removed → rejected (admin removed approved post)
//   * kind:   photo|portfolio → portfolio_photo; video → full_video;
//             reel → highlight_video; before_after → before_after
//   * caption → description; image_url → photoUrl;
//     before_url/after_url → beforePhotoUrl/afterPhotoUrl;
//     video_url → videoUrl; tags pass-through;
//     event_date → date; admin_notes → adminNotes

import { postsApi, PostStatus, PostKind } from '@/lib/postsApi';

const KEY = 'content_uploads';

// ============================================================
// Mapping helpers
// ============================================================
const FRONTEND_TO_BACKEND_KIND = {
  before_after: PostKind.BEFORE_AFTER,
  full_video: PostKind.VIDEO,
  highlight_video: PostKind.REEL,
  tournament_photo: PostKind.PHOTO,
  tournament_video: PostKind.VIDEO,
  battle_content: PostKind.PHOTO,
  portfolio_photo: PostKind.PORTFOLIO,
};

const BACKEND_TO_FRONTEND_KIND = {
  [PostKind.BEFORE_AFTER]: 'before_after',
  [PostKind.VIDEO]: 'full_video',
  [PostKind.REEL]: 'highlight_video',
  [PostKind.PHOTO]: 'portfolio_photo',
  [PostKind.PORTFOLIO]: 'portfolio_photo',
};

const BACKEND_TO_FRONTEND_STATUS = {
  [PostStatus.PENDING]: 'pending_approval',
  [PostStatus.APPROVED]: 'approved',
  [PostStatus.REJECTED]: 'rejected',
  [PostStatus.REMOVED]: 'rejected',
};

export const FRONTEND_STATUS_TO_BACKEND = {
  pending_approval: PostStatus.PENDING,
  approved: PostStatus.APPROVED,
  rejected: PostStatus.REJECTED,
  removed: PostStatus.REMOVED,
};

// Build the upload-create payload sent to the backend.
export const uploadToBackendPayload = (form) => {
  const kind = FRONTEND_TO_BACKEND_KIND[form.contentType] || PostKind.PHOTO;
  const payload = {
    kind,
    caption: form.description || '',
    tags: Array.isArray(form.tags) ? form.tags : [],
    category: form.category || null,
    location: form.location || null,
    event_date: form.date || null,
  };
  if (kind === PostKind.BEFORE_AFTER) {
    payload.before_url = form.beforePhotoUrl || null;
    payload.after_url = form.afterPhotoUrl || null;
    payload.image_url = form.afterPhotoUrl || null; // primary preview
  } else if (kind === PostKind.VIDEO || kind === PostKind.REEL) {
    payload.video_url = form.videoUrl || null;
  } else {
    payload.image_url = form.photoUrl || null;
  }
  return payload;
};

// Reshape a backend post doc into the legacy upload shape.
export const backendToUpload = (be) => {
  if (!be) return be;
  return {
    id: be.id,
    __backendId: be.id,
    __postId: be.id,
    barberId: be.author_id,
    barberName: be.author_name || be.author_username,
    barberUsername: be.author_username,
    barberAvatar: be.author_avatar,
    contentType: BACKEND_TO_FRONTEND_KIND[be.kind] || 'portfolio_photo',
    category: be.category || null,
    description: be.caption || '',
    location: be.location || '',
    date: be.event_date || be.created_at?.slice(0, 10) || '',
    tags: be.tags || [],
    beforePhotoUrl: be.before_url || '',
    afterPhotoUrl: be.after_url || '',
    videoUrl: be.video_url || '',
    photoUrl: be.image_url || '',
    status: BACKEND_TO_FRONTEND_STATUS[be.status] || 'pending_approval',
    createdAt: be.created_at,
    approvedAt: be.status === PostStatus.APPROVED ? (be.moderated_at || be.updated_at) : null,
    rejectedAt: (be.status === PostStatus.REJECTED || be.status === PostStatus.REMOVED)
      ? (be.moderated_at || be.updated_at) : null,
    adminNotes: be.admin_notes || '',
    featured: Boolean(be.featured),
    likes: be.likes_count || 0,
    comments: be.comments_count || 0,
  };
};

// ============================================================
// Local cache primitives
// ============================================================
const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
};

const write = (list) => {
  try { localStorage.setItem(KEY, JSON.stringify(list)); }
  catch { /* ignore */ }
  window.dispatchEvent(new Event('lineage_uploads_updated'));
};

const upsert = (upload) => {
  if (!upload) return;
  const all = read();
  const idx = all.findIndex((u) => u.id === upload.id || u.__backendId === upload.__backendId);
  const next = idx === -1 ? [upload, ...all] : all.map((u, i) => (i === idx ? upload : u));
  write(next);
};

// ============================================================
// Reads
// ============================================================
export const getAllUploads = () => read();

export const getUploadsForUser = (userId) => {
  if (!userId) return [];
  return read().filter((u) => u.barberId === userId);
};

export const getApprovedUploadsForUser = (userId) => {
  if (!userId) return [];
  return read().filter((u) => u.barberId === userId && u.status === 'approved');
};

// ============================================================
// Writes — backend-backed
// ============================================================
export const recordUpload = async (form) => {
  const payload = uploadToBackendPayload(form);
  const created = await postsApi.create(payload);
  const normalized = backendToUpload(created);
  upsert(normalized);
  return normalized;
};

export const moderateUpload = async (uploadId, frontendStatus, notes = null) => {
  const backendStatus = FRONTEND_STATUS_TO_BACKEND[frontendStatus] || frontendStatus;
  await postsApi.moderate(uploadId, backendStatus, notes);
  // Re-fetch the post to get the canonical record (moderated_at etc.).
  try {
    const fresh = await postsApi.get(uploadId);
    const normalized = backendToUpload(fresh);
    upsert(normalized);
    return normalized;
  } catch {
    // Best-effort cache update if the GET fails.
    const all = read();
    const updated = all.map((u) => (u.id === uploadId
      ? { ...u, status: frontendStatus, adminNotes: notes || u.adminNotes }
      : u));
    write(updated);
    return null;
  }
};

export const deleteUpload = async (uploadId) => {
  await postsApi.remove(uploadId);
  const all = read().filter((u) => u.id !== uploadId);
  write(all);
};

// LIMPIEZA ago-2026 — destacar en servidor y refrescar cache local.
export const setUploadFeatured = async (uploadId, featured) => {
  const fresh = await postsApi.setFeatured(uploadId, featured);
  const normalized = backendToUpload(fresh);
  upsert(normalized);
  return normalized;
};

// ============================================================
// Hydration
// ============================================================
// Pulls the relevant set of posts into the local cache. Admins see EVERYTHING
// (pending + approved + rejected) since they moderate. Other users see only
// their own + the public approved feed.
export const hydrateUploadsFromBackend = async (user) => {
  if (!user) return;
  try {
    const isAdmin = ['admin', 'super_admin'].includes(user.role);
    const requests = [];
    requests.push(postsApi.me({ limit: 200 }).catch(() => ({ items: [] })));
    if (isAdmin) {
      requests.push(postsApi.pending({ limit: 200 }).catch(() => ({ items: [] })));
      requests.push(postsApi.list({ status: 'approved', limit: 200 }).catch(() => ({ items: [] })));
      requests.push(postsApi.list({ status: 'rejected', limit: 200 }).catch(() => ({ items: [] })));
    }
    const responses = await Promise.all(requests);
    const map = new Map();
    responses.forEach((r) => {
      (r.items || []).forEach((be) => {
        const u = backendToUpload(be);
        if (!map.has(u.id)) map.set(u.id, u);
      });
    });
    const merged = Array.from(map.values()).sort(
      (a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')
    );
    try { localStorage.setItem(KEY, JSON.stringify(merged)); } catch { /* ignore */ }
    window.dispatchEvent(new Event('lineage_uploads_updated'));
    return merged;
  } catch {
    return null;
  }
};
